<script setup lang="ts">
import {computed, ref} from "vue";
import {CirclePlus, MessageSquareText, Stethoscope, Trash2} from "@lucide/vue";
import {Input} from "@/core/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import BaseProductSearchBar from "@/product/components/BaseProductSearchBar.vue";
import {LegacyProduct} from "@/core/interfaces/Product";
import {namesProduct, requiresOutcome} from "@/medication-checkup/services/InterventionStatuses";
import {CARD_SHEET, pillClasses, STATUS_CHIPS} from "@/medication-checkup/services/SceneClasses";
import {
    InterventionProduct,
    InterventionStatus,
    InterventionStatusOption
} from "@/medication-checkup/interfaces/interventions";
import {
    DoctorArbitration,
    DoctorDecisionDraft,
    DoctorDecisionKey,
    DoctorDecisionOption,
    DoctorFeedbackAddable,
    DoctorFeedbackRow,
    DoctorInitiative,
    DoctorInitiativeDraft
} from "@/medication-checkup/interfaces/doctor-feedback";

/**
 * The card of the doctor's answer: one row per proposal that was
 * transmitted, across four columns — the treatment with its posology, what
 * was proposed, the decision of the doctor, the actions — and, under a row,
 * what the decision unfolds: the counter-proposal being written, the
 * comment.
 *
 * The table opens blank. Nothing is pre-arbitrated, and a row left untouched
 * writes nothing: the doctor does not answer line by line, and what they
 * did not mention is an absence, not a refusal. Clicking a pill already
 * chosen takes the row back to that silence.
 *
 * The changes at the doctor's initiative close the table, under a rule of
 * their own: there is nothing to arbitrate on a line nobody proposed, so
 * they wear a chip where the others wear pills.
 *
 * Every word comes served: the rows, the decisions, the statuses. Drafting
 * is the scene's state, handed to the card while live; the frozen card reads
 * the decisions the record holds.
 */

const props = defineProps<{
    rows: DoctorFeedbackRow[];
    addable: DoctorFeedbackAddable[];
    decisions: DoctorDecisionOption[];
    statuses: InterventionStatusOption[];
    frozen?: boolean;
    /**
     * What the pharmacist recorded on each row, by row key, while live. A
     * row without an entry is one the doctor said nothing about.
     */
    drafts?: Record<string, DoctorDecisionDraft>;
    /**
     * The changes at the doctor's initiative while live; the frozen card
     * reads `initiatives` instead.
     */
    ownChanges?: DoctorInitiativeDraft[];
    initiatives?: DoctorInitiative[];
    busy?: boolean;
}>();

const emit = defineEmits<{
    'update:draft': [key: string, draft: DoctorDecisionDraft | null];
    add: [initiative: DoctorInitiativeDraft];
    'update:initiative': [initiative: DoctorInitiativeDraft];
    'remove-initiative': [key: string];
}>();

/**
 * The columns of the table. The decision takes a fixed width, sized to hold
 * the three pills on one line — they measure 202px together in Poppins
 * SemiBold at 10.5px, so the column is not to be narrowed below that. What
 * was proposed carries a chip and a line, and leaves the rest to the
 * treatment, whose names run long.
 */
const GRID = 'grid grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)_216px_44px] gap-3 px-4 border-t border-gray-light';

const SUBROW = 'col-span-full flex items-center gap-1.5';

const SUBINPUT = 'flex-1 min-w-0 h-7 !border-solid !border-gray bg-white px-2 py-1 text-[11.5px] shadow-none';

/**
 * The tone a decision is read by once taken — softer than the pill that
 * takes it, since the frozen table reads rather than asks.
 */
const DECISION_CHIPS: Record<DoctorDecisionKey, string> = {
    accepted: 'bg-secondary-lightest text-secondary-darkest',
    amended: 'bg-orange/30 text-gray-dark',
    refused: 'bg-red-light/15 text-red-light',
    initiated: 'bg-lightPurple-lightest text-primary'
};

/**
 * The tone a pill wears while it stands: the mint of a confirmation, the
 * orange of something to keep an eye on, the coral of a refusal.
 */
const DECISION_PILLS: Record<DoctorArbitration, string> = {
    accepted: 'bg-secondary text-primary-dark border-secondary',
    amended: 'bg-orange text-gray-dark border-orange',
    refused: 'bg-red-light text-white border-red-light'
};

/**
 * The treatment picked in the footer, held until its row is opened.
 */
const picked = ref<string>('');

/**
 * Remounting the search bar is how it forgets a selection.
 */
const searchGeneration = ref(0);

const ownChanges = computed(() => props.ownChanges ?? []);

const draftOf = (row: DoctorFeedbackRow): DoctorDecisionDraft | null => props.drafts?.[row.key] ?? null;

/**
 * What a row reads as: the draft while live, the record once frozen.
 */
const statusOf = (row: DoctorFeedbackRow): InterventionStatus | null => {
    return props.frozen ? (row.decision?.status ?? null) : (draftOf(row)?.status ?? null);
};

const outcomeOf = (row: DoctorFeedbackRow): string => {
    return props.frozen ? (row.decision?.outcome ?? '') : (draftOf(row)?.outcome ?? '');
};

const commentOf = (row: DoctorFeedbackRow): string => {
    return props.frozen ? (row.decision?.comment ?? '') : (draftOf(row)?.comment ?? '');
};

const productOf = (row: DoctorFeedbackRow): InterventionProduct | null => {
    return props.frozen ? (row.decision?.product ?? null) : (draftOf(row)?.product ?? null);
};

/**
 * Take a decision on a row, or take it back: clicking the pill that already
 * stands returns the row to the silence it opened on.
 */
const decide = (row: DoctorFeedbackRow, decision: DoctorArbitration) => {
    if (draftOf(row)?.decision === decision) {
        return emit('update:draft', row.key, null);
    }

    emit('update:draft', row.key, {
        decision,
        status: null,
        product: null,
        outcome: '',
        comment: draftOf(row)?.comment ?? ''
    });
};

const update = (row: DoctorFeedbackRow, changes: Partial<DoctorDecisionDraft>) => {
    const draft = draftOf(row);

    if (draft !== null) {
        emit('update:draft', row.key, {...draft, ...changes});
    }
};

/**
 * Setting the status of a counter-proposal drops what the previous one
 * carried: a stop keeps no posology, a change of posology no product.
 */
const setStatus = (row: DoctorFeedbackRow, status: InterventionStatus) => {
    update(row, {
        status,
        product: namesProduct(status) ? draftOf(row)?.product ?? null : null,
        outcome: requiresOutcome(status) ? draftOf(row)?.outcome ?? '' : ''
    });
};

const setInitiativeStatus = (initiative: DoctorInitiativeDraft, status: InterventionStatus) => {
    emit('update:initiative', {
        ...initiative,
        status,
        outcome: requiresOutcome(status) ? initiative.outcome : ''
    });
};

const product = (legacy: LegacyProduct): InterventionProduct => ({id: legacy.id, label: legacy.name});

/**
 * Open a row for a treatment of the review the doctor changed although
 * nothing was proposed about it.
 */
const addFromReview = (value: string) => {
    const entry = props.addable.find(candidate => String(candidate.intervention_id) === value);

    picked.value = '';

    if (entry === undefined) {
        return;
    }

    emit('add', {
        key: `initiated:${Date.now()}`,
        intervention_id: entry.intervention_id,
        product: null,
        label: entry.label ?? '',
        before: entry.before,
        status: null,
        outcome: '',
        comment: ''
    });
};

/**
 * Open a row for a product the review knows nothing about, which is
 * necessarily an addition.
 */
const addFromReferential = (legacy: LegacyProduct) => {
    const picked = product(legacy);

    searchGeneration.value += 1;

    emit('add', {
        key: `initiated:${Date.now()}`,
        intervention_id: null,
        product: picked,
        label: picked.label,
        before: null,
        status: 'added',
        outcome: '',
        comment: ''
    });
};

/**
 * The treatments still open to a change of the doctor's: the ones already
 * carried by a row of their own are out.
 */
const addableLeft = computed(() => {
    const taken = ownChanges.value.map(change => change.intervention_id);

    return props.addable.filter(entry => !taken.includes(entry.intervention_id));
});

/**
 * How many of the transmitted proposals the doctor has spoken about.
 */
const arbitrated = computed(() => {
    return props.frozen
        ? props.rows.filter(row => row.decision !== null).length
        : props.rows.filter(row => draftOf(row) !== null).length;
});

const statusLabel = (status: InterventionStatus): string => {
    return props.statuses.find(option => option.key === status)?.label ?? status;
};

/**
 * The tone the pill of a counter-proposal wears. The trigger class carries
 * no ground of its own, so an unanswered status takes the neutral of the
 * table rather than none at all.
 */
const statusTone = (status: InterventionStatus | null): string => {
    return status === null ? 'bg-gray-light text-muted-foreground' : STATUS_CHIPS[status];
};

/**
 * Whether the posology the treatment carries is struck out: a change of
 * posology is the one decision that unsays it, whoever decided it.
 */
const supersedes = (row: DoctorFeedbackRow): boolean => {
    return (statusOf(row) ?? row.proposal.status) === 'posology_modified';
};

const struck = (row: DoctorFeedbackRow): boolean => {
    return (statusOf(row) ?? row.proposal.status) === 'stopped';
};

/**
 * What the proposal said in words: the product that would replace the
 * treatment, or the posology proposed for it.
 */
const proposed = (row: DoctorFeedbackRow): string | null => {
    return row.proposal.product?.label ?? row.proposal.outcome;
};

const initiatives = computed<DoctorInitiativeDraft[]>(() => {
    if (!props.frozen) {
        return ownChanges.value;
    }

    return (props.initiatives ?? []).map(initiative => ({
        key: initiative.key,
        intervention_id: initiative.intervention_id,
        product: initiative.product,
        label: initiative.label ?? '',
        before: initiative.before,
        status: initiative.status,
        outcome: initiative.outcome ?? '',
        comment: initiative.comment ?? ''
    }));
});
</script>

<template>
  <div :class="CARD_SHEET" class="rounded-xl overflow-hidden">
    <div class="flex items-center gap-2 px-4 py-3">
      <Stethoscope class="w-[18px] h-[18px] text-primary" />
      <span class="text-[13px] font-semibold">Retour du médecin traitant</span>
      <span
        v-if="!frozen"
        class="text-[11.5px] text-muted-foreground"
      >— consignez ce qu'il a décidé</span>
      <span class="flex-1" />
      <span class="text-xs font-semibold text-primary">
        {{ arbitrated }} / {{ rows.length }} arbitrée{{ rows.length > 1 ? 's' : '' }}
      </span>
    </div>

    <div :class="GRID" class="py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
      <span>Traitement</span>
      <span>Proposition transmise</span>
      <span>Décision du médecin</span>
      <span />
    </div>

    <div
      v-for="(row, index) in rows"
      :key="row.key"
      :class="GRID"
      class="py-2.5 items-start animate-message-in"
      :style="{animationDelay: `${index * 60}ms`}"
    >
      <span class="flex flex-col gap-1 min-w-0">
        <span
          class="text-xs font-semibold leading-tight"
          :class="{'line-through': struck(row)}"
        >{{ row.label }}</span>
        <span
          class="text-[10.5px] text-muted-foreground"
          :class="{'line-through': supersedes(row)}"
        >{{ row.before ?? 'Posologie non renseignée' }}</span>
      </span>

      <span class="flex flex-col gap-1 min-w-0 text-[11px] leading-snug">
        <span class="self-start px-2 py-1 rounded-full text-[10.5px] font-bold" :class="STATUS_CHIPS[row.proposal.status]">
          {{ row.proposal.status_label }}
        </span>
        <span
          v-if="proposed(row)"
          class="text-[10.5px] text-muted-foreground truncate"
          :title="proposed(row)!"
        >→ {{ proposed(row) }}</span>
        <span
          v-if="row.proposal.comment"
          class="text-[10.5px] italic text-muted-foreground"
        >{{ row.proposal.comment }}</span>
      </span>

      <span class="flex flex-col gap-1.5 min-w-0">
        <span v-if="!frozen" class="flex flex-wrap gap-1">
          <button
            v-for="option in decisions"
            :key="option.key"
            class="rounded-full !border-solid border-[1px] px-2 py-1 text-[10.5px] font-semibold transition-colors disabled:opacity-50"
            :class="draftOf(row)?.decision === option.key ? DECISION_PILLS[option.key] : pillClasses(false)"
            :disabled="busy"
            @click="decide(row, option.key)"
          >
            {{ option.label }}
          </button>
        </span>
        <span
          v-else-if="row.decision"
          class="self-start px-2 py-1 rounded-full text-[10.5px] font-bold"
          :class="DECISION_CHIPS[row.decision.decision]"
        >{{ row.decision.decision_label }}</span>
        <span v-else class="text-[10.5px] italic text-muted-foreground">Sans réponse</span>

        <Select
          v-if="!frozen && draftOf(row)?.decision === 'amended'"
          :model-value="draftOf(row)?.status ?? ''"
          :disabled="busy"
          @update:model-value="setStatus(row, $event as InterventionStatus)"
        >
          <SelectTrigger
            class="h-7 w-full rounded-full border-0 px-2 text-[10.5px] font-bold shadow-none focus:ring-0"
            :class="statusTone(statusOf(row))"
          >
            <SelectValue placeholder="Ce qu'il a décidé…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="option in statuses.filter(option => option.key !== 'added')"
              :key="option.key"
              :value="option.key"
              class="text-xs"
            >
              {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>
        <span
          v-else-if="statusOf(row)"
          class="self-start px-2 py-1 rounded-full text-[10.5px] font-bold"
          :class="STATUS_CHIPS[statusOf(row)!]"
        >{{ statusLabel(statusOf(row)!) }}</span>

        <span
          v-if="productOf(row)"
          class="text-[10.5px] text-muted-foreground truncate"
          :title="productOf(row)!.label"
        >→ {{ productOf(row)!.label }}</span>

        <span
          v-if="frozen && outcomeOf(row).trim() !== ''"
          class="text-[10.5px] text-muted-foreground truncate"
          :title="outcomeOf(row)"
        >→ {{ outcomeOf(row) }}</span>
      </span>

      <span v-if="!frozen && draftOf(row)" class="flex justify-end pt-0.5">
        <button
          class="text-muted-foreground hover:text-red-500 disabled:opacity-50"
          :disabled="busy"
          title="Retirer la décision"
          @click="emit('update:draft', row.key, null)"
        >
          <Trash2 class="w-[15px] h-[15px]" />
        </button>
      </span>
      <span v-else />

      <template v-if="!frozen && draftOf(row)?.decision === 'amended'">
        <span v-if="statusOf(row) && namesProduct(statusOf(row)!)" :class="SUBROW">
          <CirclePlus class="w-3.5 h-3.5 shrink-0 text-primary" />
          <span
            v-if="productOf(row)"
            class="flex-1 min-w-0 truncate rounded-md border border-slate-200 bg-white px-2 py-1 text-[11.5px] font-semibold text-primary"
          >{{ productOf(row)!.label }}</span>
          <BaseProductSearchBar
            v-else
            class="flex-1"
            :disabled="busy"
            placeholder="Produit décidé par le médecin…"
            @select="update(row, {product: product($event)})"
          />
        </span>
        <span v-if="statusOf(row) && requiresOutcome(statusOf(row)!)" :class="SUBROW">
          <Input
            :model-value="outcomeOf(row)"
            class="flex-1 min-w-0 h-7 !border !border-solid !border-primary bg-white px-2 py-1 text-[11.5px] shadow-none"
            :disabled="busy"
            placeholder="Posologie décidée (ex. 500 mg matin et soir)…"
            @update:model-value="update(row, {outcome: String($event)})"
          />
        </span>
      </template>

      <span v-if="!frozen && draftOf(row)" :class="SUBROW">
        <MessageSquareText class="w-3.5 h-3.5 shrink-0 text-primary" />
        <Input
          :model-value="commentOf(row)"
          :class="SUBINPUT"
          :disabled="busy"
          :placeholder="draftOf(row)?.decision === 'refused' ? 'Motif du refus (facultatif)…' : 'Commentaire du médecin (facultatif)…'"
          @update:model-value="update(row, {comment: String($event)})"
        />
      </span>
      <span v-else-if="frozen && commentOf(row).trim() !== ''" :class="SUBROW">
        <MessageSquareText class="w-3.5 h-3.5 shrink-0 text-primary" />
        <span class="text-[11px] italic text-muted-foreground">{{ commentOf(row) }}</span>
      </span>
    </div>

    <div
      v-for="entry in initiatives"
      :key="entry.key"
      :class="GRID"
      class="py-2.5 items-start border-l-2 border-l-primary animate-message-in"
    >
      <span class="flex flex-col gap-1 min-w-0">
        <span
          class="text-xs font-semibold leading-tight"
          :class="{'line-through': entry.status === 'stopped'}"
        >{{ entry.label }}</span>
        <span
          class="text-[10.5px] text-muted-foreground"
          :class="{'line-through': entry.status === 'posology_modified'}"
        >{{ entry.before ?? '—' }}</span>
      </span>

      <span class="text-[11px] italic text-muted-foreground">Non proposé au médecin</span>

      <span class="flex flex-col gap-1.5 min-w-0">
        <span
          class="self-start px-2 py-1 rounded-full text-[10.5px] font-bold"
          :class="DECISION_CHIPS.initiated"
        >À l'initiative du médecin</span>

        <Select
          v-if="!frozen && entry.intervention_id !== null"
          :model-value="entry.status ?? ''"
          :disabled="busy"
          @update:model-value="setInitiativeStatus(entry, $event as InterventionStatus)"
        >
          <SelectTrigger
            class="h-7 w-full rounded-full border-0 px-2 text-[10.5px] font-bold shadow-none focus:ring-0"
            :class="statusTone(entry.status)"
          >
            <SelectValue placeholder="Ce qu'il a décidé…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="option in statuses.filter(option => option.key !== 'added')"
              :key="option.key"
              :value="option.key"
              class="text-xs"
            >
              {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>
        <span
          v-else-if="entry.status"
          class="self-start px-2 py-1 rounded-full text-[10.5px] font-bold"
          :class="STATUS_CHIPS[entry.status]"
        >{{ statusLabel(entry.status) }}</span>

        <span
          v-if="frozen && entry.outcome.trim() !== ''"
          class="text-[10.5px] text-muted-foreground truncate"
          :title="entry.outcome"
        >→ {{ entry.outcome }}</span>
      </span>

      <span v-if="!frozen" class="flex justify-end pt-0.5">
        <button
          class="text-muted-foreground hover:text-red-500 disabled:opacity-50"
          :disabled="busy"
          title="Retirer ce changement"
          @click="emit('remove-initiative', entry.key)"
        >
          <Trash2 class="w-[15px] h-[15px]" />
        </button>
      </span>
      <span v-else />

      <span v-if="!frozen && entry.status && requiresOutcome(entry.status)" :class="SUBROW">
        <Input
          :model-value="entry.outcome"
          class="flex-1 min-w-0 h-7 !border !border-solid !border-primary bg-white px-2 py-1 text-[11.5px] shadow-none"
          :disabled="busy"
          placeholder="Posologie décidée…"
          @update:model-value="emit('update:initiative', {...entry, outcome: String($event)})"
        />
      </span>

      <span v-if="!frozen" :class="SUBROW">
        <MessageSquareText class="w-3.5 h-3.5 shrink-0 text-primary" />
        <Input
          :model-value="entry.comment"
          :class="SUBINPUT"
          :disabled="busy"
          placeholder="Commentaire du médecin (facultatif)…"
          @update:model-value="emit('update:initiative', {...entry, comment: String($event)})"
        />
      </span>
      <span v-else-if="entry.comment.trim() !== ''" :class="SUBROW">
        <MessageSquareText class="w-3.5 h-3.5 shrink-0 text-primary" />
        <span class="text-[11px] italic text-muted-foreground">{{ entry.comment }}</span>
      </span>
    </div>

    <div v-if="!frozen" class="flex items-center gap-2 px-4 py-2.5 border-t border-gray-light bg-gray-light">
      <CirclePlus class="w-[17px] h-[17px] shrink-0 text-primary" />
      <span class="text-[11.5px] font-semibold text-gray-dark">Changement décidé par le médecin</span>
      <Select
        v-if="addableLeft.length > 0"
        :model-value="picked"
        :disabled="busy"
        @update:model-value="addFromReview(String($event))"
      >
        <SelectTrigger class="h-8 w-[200px] rounded-md border-gray bg-white px-2.5 text-xs shadow-none focus:ring-0">
          <SelectValue placeholder="Un traitement du bilan" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="entry in addableLeft"
            :key="entry.intervention_id"
            :value="String(entry.intervention_id)"
            class="text-xs"
          >
            {{ entry.label }}
          </SelectItem>
        </SelectContent>
      </Select>
      <BaseProductSearchBar
        :key="searchGeneration"
        class="flex-1"
        :disabled="busy"
        placeholder="ou un produit hors bilan"
        @select="addFromReferential"
      />
    </div>
  </div>
</template>
